import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form'
import { Grid, Segment, Table } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import IrrigationEventModal from './IrrigationEventModal'
import IrrigationEvent from './IrrigationEvent'

interface IrrigationEventsTableProps {
  waterUnitOptions: Array<{ key: number; value: number; text: string }>
  waterSourceOptions: Array<{ key: number; value: number; text: string }>
  powerSourceOptions: Array<{ key: number; value: number; text: string }>
  depthUnitOptions: Array<{ key: number; value: number; text: string }>
  distanceUnitOptions: Array<{ key: number; value: number; text: string }>
}

const defaultKeys = [
  'defaultIrrigationDepth',
  'defaultIrrigationDepthUnit',
  'defaultIrrigationDistance',
  'defaultIrrigationDistanceUnit',
  'defaultIrrigationPowerSource',
  'defaultIrrigationWaterSource',
  'defaultIrrigationMethod'
]

const IrrigationEventsTable: React.FC<IrrigationEventsTableProps> = ({
  waterUnitOptions,
  waterSourceOptions,
  powerSourceOptions,
  depthUnitOptions,
  distanceUnitOptions
}) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null)

  const numberOfEvents = useWatch({ control, name: 'numberOfIrrigationEvents' }) || 0

  const [
    watchDefPumpingDepth,
    watchDefPumpingDepthUnit,
    watchDefHorizantalDistance,
    watchDefHorizontalDistanceUnit,
    watchDefPowerSource,
    watchDefWaterSource,
    watchMethod
  ] = useWatch({ control, name: defaultKeys })

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'irrigationEvents',
    keyName: 'key'
  })

  useEffect(() => {
    const currentLength = fields.length
    const targetLength = numberOfEvents

    if(watchDefPumpingDepth &&
      watchDefPumpingDepthUnit &&
      watchDefHorizantalDistance &&
      watchDefHorizontalDistanceUnit &&
      watchDefPowerSource &&
      watchDefWaterSource &&
      watchMethod
    ) {

      if (targetLength > currentLength) {
        for (let i = currentLength; i < targetLength; i++) {
          append({
            addedWater: '',
            addedWaterUnit: '',
            irrigatedArea: '',
            method: watchMethod,
            depth: watchDefPumpingDepth,
            depthUnit: watchDefPumpingDepthUnit,
            distance: watchDefHorizantalDistance,
            distanceUnit: watchDefHorizontalDistanceUnit,
            powerSource: watchDefPowerSource,
            waterSource: watchDefWaterSource
          })
        }
      } else if (targetLength < currentLength) {
        for (let i = currentLength - 1; i >= targetLength; i--) {
          remove(i)
        }
      }
    }
  }, [
    numberOfEvents,
    watchDefPumpingDepth,
    watchDefPumpingDepthUnit,
    watchDefHorizantalDistance,
    watchDefHorizontalDistanceUnit,
    watchDefPowerSource,
    watchDefWaterSource,
    watchMethod
  ])

  const handleOpenModal = (index: number) => {
    setSelectedEventIndex(index)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedEventIndex(null)
  }

  if (numberOfEvents <= 0 && fields.length === 0) {
    return null
  }

  return (
    <>
      <Grid.Row>
        <Grid.Column width={16}>
          <Segment>
            <Table celled>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{'#'}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Total water added')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Unit')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('% area irrigated')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('litre / ha')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Actions')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {fields.map((field, index) => (
                  <Table.Row key={field.key}>
                    <IrrigationEvent
                      index={index}
                      handleOpenModal={handleOpenModal}
                      waterUnitOptions={waterUnitOptions}
                    />
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>
      </Grid.Row>

      {selectedEventIndex !== null && (
        <IrrigationEventModal
          open={modalOpen}
          onClose={handleCloseModal}
          eventIndex={selectedEventIndex}
          waterSourceOptions={waterSourceOptions}
          powerSourceOptions={powerSourceOptions}
          depthUnitOptions={depthUnitOptions}
          distanceUnitOptions={distanceUnitOptions}
          handleUpdate={(value) => {
            const eventData = fields[selectedEventIndex]
            update(selectedEventIndex, {
              ...eventData,
              ...value
            })
            handleCloseModal()
          }}
        />
      )}
    </>
  )
}

export default IrrigationEventsTable
